import React from 'react';
import { HANDBUCH_FARBEN } from './stil';

/**
 * Ein umrandeter Abschnitt mit Ueberschriftsbalken — der Baustein, aus dem
 * die Kapitelraster bestehen (Gewerkeseite: 2 × 2, Deckblatt: gestapelt).
 *
 * Portiert aus `components/document/SectionBox.tsx` der Quell-App. Deren
 * Markup verliess sich auf zwei globale Klassen: `border-[var(--color-border)]`
 * und `.section-header`. Beide gibt es in OS-IA nicht und sollen es auch nicht
 * geben — die Begruendung steht in `stil.ts`. Die Werte kommen deshalb aus
 * `HANDBUCH_FARBEN` und stehen als Inline-Stil an genau dieser Stelle.
 */

interface Props {
    titel: string;
    /** Rechts im Ueberschriftsbalken, z. B. eine Zaehlung („4 von 6"). */
    zusatz?: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
    children: React.ReactNode;
}

export const Abschnitt: React.FC<Props> = ({ titel, zusatz, className = '', style, children }) => (
    <div
        className={`flex flex-col min-h-0 ${className}`}
        style={{ border: `1px solid ${HANDBUCH_FARBEN.linie}`, ...style }}
    >
        <div
            className="flex items-baseline justify-between gap-2 shrink-0"
            style={{
                background: HANDBUCH_FARBEN.abschnittHintergrund,
                padding: '3px 8px',
                fontWeight: 700,
                fontSize: '10px',
                textTransform: 'uppercase',
                letterSpacing: '0.4px',
                borderBottom: `1px solid ${HANDBUCH_FARBEN.linie}`,
            }}
        >
            <span className="truncate">{titel}</span>
            {zusatz !== undefined && (
                <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
                    {zusatz}
                </span>
            )}
        </div>
        {/* `overflow-hidden` und nicht `overflow-auto`: auf Papier gibt es
            keine Bildlaufleiste. Was hier abgeschnitten wird, muss der
            Bearbeiter sehen — und kuerzen. */}
        <div className="flex-1 min-h-0 overflow-hidden" style={{ padding: '5px' }}>
            {children}
        </div>
    </div>
);

/**
 * Die Tabelle innerhalb eines Abschnitts.
 *
 * Steht hier und nicht als eigene Datei, weil sie ohne `Abschnitt` nirgends
 * vorkommt. Ersetzt die globale Klasse `.doc-table` der Quell-App.
 */
export const AbschnittTabelle: React.FC<{
    kopf: React.ReactNode[];
    children: React.ReactNode;
    /** Spaltenbreiten als CSS-Werte, in Spaltenreihenfolge. */
    breiten?: string[];
}> = ({ kopf, children, breiten }) => (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px' }}>
        {breiten && (
            <colgroup>
                {breiten.map((b, i) => <col key={i} style={{ width: b }} />)}
            </colgroup>
        )}
        <thead>
            <tr>
                {kopf.map((z, i) => (
                    <th
                        key={i}
                        style={{
                            textAlign: 'left',
                            padding: '3px 5px',
                            fontSize: '8px',
                            borderBottom: `1px solid ${HANDBUCH_FARBEN.linie}`,
                        }}
                    >
                        {z}
                    </th>
                ))}
            </tr>
        </thead>
        <tbody>{children}</tbody>
    </table>
);

/** Eine Zeile der Abschnittstabelle. Die Trennlinie sitzt oben und nicht
 *  unten, damit unter der letzten Zeile keine freischwebende Linie steht. */
export const AbschnittZeile: React.FC<{ erste?: boolean; children: React.ReactNode }> = ({
    erste = false, children,
}) => (
    <tr style={erste ? undefined : { borderTop: '1px solid #dddddd' }}>
        {children}
    </tr>
);

/** Eine Zelle der Abschnittstabelle. */
export const AbschnittZelle: React.FC<{
    children?: React.ReactNode;
    style?: React.CSSProperties;
}> = ({ children, style }) => (
    <td style={{ padding: '2px 5px', verticalAlign: 'top', ...style }}>{children}</td>
);

/**
 * Der Platz eines Abschnitts, an dem es (noch) nichts zu zeigen gibt.
 *
 * Bewusst ein SATZ und kein leerer Kasten: ein leerer Abschnitt im gedruckten
 * Handbuch behauptet, es gebe hier nichts zu regeln. „Nicht erfasst" und
 * „nicht erforderlich" sind zwei verschiedene Aussagen, und nur eine davon
 * darf das Dokument treffen.
 */
export const AbschnittLeer: React.FC<{ text?: string }> = ({
    text = 'Noch nicht erfasst.',
}) => (
    <div style={{ fontSize: '8.5px', fontStyle: 'italic', color: HANDBUCH_FARBEN.textLeise }}>
        {text}
    </div>
);
